/**
 * HTTP client for medical test location detail search (Site NPI modal).
 *
 * @module siteNpiDetailsService
 */
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { getBearerToken } from "../../postCall/bearerToken";
import { throwApiError } from "../../throwApiError";
import { unwrapBackendListData } from "../../unwrapBackendListData";
import {
  ListTestLocationDetailsParams,
  SearchTestLocationDetailsParams,
  TestLocationDetail,
  UpsertTestLocationDetailsPayload,
  UpsertTestLocationDetailsResponse,
} from "./siteNpiDetailsTypes";

/** Default page size for org test location list requests. */
export const DEFAULT_TEST_LOCATION_LIST_LIMIT = 500;

/** Default pagination offset for org test location list requests. */
export const DEFAULT_TEST_LOCATION_LIST_OFFSET = 0;

/**
 * Builds the query string for org test location list requests.
 *
 * @param params - Optional `limit` and `offset`; defaults to 500 and 0.
 * @returns Encoded query string without a leading `?`.
 */
export const buildTestLocationDetailsListQuery = (
  params: ListTestLocationDetailsParams = {},
): string => {
  const searchParams = new URLSearchParams();
  searchParams.set(
    "limit",
    String(params.limit ?? DEFAULT_TEST_LOCATION_LIST_LIMIT),
  );
  searchParams.set(
    "offset",
    String(params.offset ?? DEFAULT_TEST_LOCATION_LIST_OFFSET),
  );
  return searchParams.toString();
};

/**
 * Builds the query string for test location detail search.
 *
 * @param params - Optional search substring and result limit.
 * @returns Encoded query string without a leading `?`.
 */
export const buildTestLocationDetailsSearchQuery = (
  params: SearchTestLocationDetailsParams,
): string => {
  const searchParams = new URLSearchParams();

  if (params.q !== undefined && params.q !== "") {
    searchParams.set("q", params.q);
  }
  if (params.limit !== undefined) {
    searchParams.set("limit", String(params.limit));
  }

  return searchParams.toString();
};

/**
 * Authenticated fetch for test location detail endpoints.
 *
 * Attaches the bearer token and standard JSON accept header, then throws via
 * {@link throwApiError} when the response is not OK.
 *
 * @param url - Fully resolved request URL.
 * @param init - Fetch options (method, body, signal, extra headers).
 * @returns The successful fetch Response.
 * @throws Error when auth is missing or the HTTP response is not OK.
 */
const requestTestLocationDetails = async (
  url: string,
  init: RequestInit = {},
): Promise<Response> => {
  const bearerToken = await getBearerToken();
  if (!bearerToken) {
    throw new Error("Authentication token unavailable");
  }

  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  headers.set("Authorization", `Bearer ${bearerToken}`);

  const response = await fetch(url, {
    ...init,
    headers,
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return response;
};

/**
 * Service for test location detail list, search, and upsert used by Site NPI UI.
 */
export class SiteNpiDetailsService {
  /**
   * Returns test location details for the authenticated user's organization.
   *
   * @param params - Optional `limit` (default 500) and `offset` (default 0).
   * @param signal - Optional abort signal for in-flight cancellation.
   * @returns Location rows for the org from the backend list envelope.
   * @throws Error when auth is missing, HTTP fails, or the body is not a list envelope.
   */
  static async getAllTestLocationForOrg(
    params: ListTestLocationDetailsParams = {},
    signal?: AbortSignal,
  ): Promise<TestLocationDetail[]> {
    const query = buildTestLocationDetailsListQuery(params);
    const response = await requestTestLocationDetails(
      `${API_ENDPOINTS.TEST_LOCATION_DETAILS}?${query}`,
      { method: "GET", signal },
    );

    const body: unknown = await response.json();
    return unwrapBackendListData<TestLocationDetail>(
      body,
      response.status,
      "Test location details",
    );
  }

  /**
   * Searches test location details by alias, name, NPI, address, or city.
   *
   * @param params - Optional `q` filter and `limit` cap.
   * @param signal - Optional abort signal for debounced or superseded requests.
   * @returns Matching location rows from the backend list envelope.
   * @throws Error when auth is missing, HTTP fails, or the body is not a list envelope.
   */
  static async searchTestLocationDetails(
    params: SearchTestLocationDetailsParams = {},
    signal?: AbortSignal,
  ): Promise<TestLocationDetail[]> {
    const query = buildTestLocationDetailsSearchQuery(params);
    const url = query
      ? `${API_ENDPOINTS.TEST_LOCATION_DETAILS_SEARCH}?${query}`
      : API_ENDPOINTS.TEST_LOCATION_DETAILS_SEARCH;

    const response = await requestTestLocationDetails(url, {
      method: "GET",
      signal,
    });

    const body: unknown = await response.json();
    return unwrapBackendListData<TestLocationDetail>(
      body,
      response.status,
      "Test location details",
    );
  }

  /**
   * Creates or updates a test location detail record.
   *
   * @param payload - Site name, alias, address, city, state, zip, and NPI.
   * @param signal - Optional abort signal for in-flight cancellation.
   * @returns Parsed upsert response from the backend.
   * @throws Error when auth is missing, HTTP fails, or the request is aborted.
   */
  static async upsertTestLocationDetails(
    payload: UpsertTestLocationDetailsPayload,
    signal?: AbortSignal,
  ): Promise<UpsertTestLocationDetailsResponse> {
    const response = await requestTestLocationDetails(
      API_ENDPOINTS.TEST_LOCATION_DETAILS_UPSERT,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal,
      },
    );

    return (await response.json()) as UpsertTestLocationDetailsResponse;
  }
}
